/**
 * Minimum adaptive datalabel font size in pixels.
 * Preserves the historical baseline so small tiles render as before.
 */
export const DATALABEL_FONT_SIZE_MIN = 10;

/**
 * Maximum adaptive datalabel font size in pixels.
 * Layout padding is reserved against this bound so edge-anchored labels
 * cannot clip on large tiles.
 */
export const DATALABEL_FONT_SIZE_MAX = 18;

/**
 * Chart-area dimension (px) at which the font reaches DATALABEL_FONT_SIZE_MIN.
 * Below this the font is clamped to the minimum; above it grows linearly
 * until clamped to DATALABEL_FONT_SIZE_MAX.
 */
const REFERENCE_DIMENSION = 300;

/**
 * Computes a datalabel font size that scales with the chart plotting area.
 * Uses min(width, height) so portrait and landscape tiles behave symmetrically.
 * Returns the minimum when chartArea is not yet available (first layout pass).
 */
export function computeAdaptiveDatalabelFontSize(
  chartArea: { width: number; height: number } | undefined,
): number {
  if (!chartArea || !chartArea.width || !chartArea.height) {
    return DATALABEL_FONT_SIZE_MIN;
  }

  const dim = Math.min(chartArea.width, chartArea.height);
  const scaled = (dim / REFERENCE_DIMENSION) * DATALABEL_FONT_SIZE_MIN;
  const clamped = Math.min(
    DATALABEL_FONT_SIZE_MAX,
    Math.max(DATALABEL_FONT_SIZE_MIN, scaled),
  );
  return Math.round(clamped);
}
